'use client';

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';

const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000';

// Карта конфигураций для каждой коллекции
const collectionUrlMap = {
  basic: {
    baseUrl: 'https://icons-pbl.davooda.com/basic-icons/',
    imageSuffix: '.svg',
    type: 'svg',
    urlPrefix: '/basic-icons',
  },
  outline: {
    baseUrl: 'https://icons-pbl.davooda.com/outline/png-256px/',
    imageSuffix: '-256x256.png',
    type: 'png',
    urlPrefix: '/outline',
  },
  'polish-blue': {
    baseUrl: 'https://icons-pbl.davooda.com/polish-blue/png-256px/',
    imageSuffix: '-256x256.png',
    type: 'png',
    urlPrefix: '/polish-blue-icons',
  },
  'basic3d': {
    baseUrl: 'https://icons-pbl.davooda.com/basic3d-icons/png-256px/',
    imageSuffix: '.png',
    type: 'png',
    urlPrefix: '/basic3d',
  },
};

// Collection paths for the link URLs
const collectionLinkPaths = {
  basic: '/basic-icons/',
  outline: '/outline-icons/',
  'polish-blue': '/polish-blue-icons/',
  'basic3d': '/basic3d/',  // Fixed link path for basic3d
};

// Начальное количество отображаемых иконок для каждой коллекции
const collectionInitialVisibleCount = {
  basic: 19,
  'polish-blue': 19,
  outline: 19,
  'basic3d': 19,
};

// Define the specific order for collections
const collectionOrder = ['basic3d', 'outline', 'basic', 'polish-blue'];

const SearchResults = ({ query, user = null }) => {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleIcons, setVisibleIcons] = useState({});

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${BACKEND_URL}/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query, user }),
        });

        if (!response.ok) {
          throw new Error('Network error while fetching search results');
        }

        const data = await response.json();

        // Логи сервера для отладки
        console.log('Server response:', data);

        // Получаем коллекции (может быть пустым объектом)
        const collections = data.collections || {};
        setResults(collections);

        // Инициализируем видимое количество иконок для каждой коллекции
        const initialVisible = {};
        Object.keys(collections).forEach((collectionName) => {
          initialVisible[collectionName] =
            collectionInitialVisibleCount[collectionName] || 20; // Если не задано, используем 20
        });
        setVisibleIcons(initialVisible);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query, user]);

  // Показать все иконки для конкретной коллекции
  const handleShowMore = (collectionName) => {
    setVisibleIcons((prev) => ({
      ...prev,
      [collectionName]: results[collectionName].length, // Отобразить все оставшиеся
    }));
  };

  // Проверяем, пусты ли все коллекции
  const allCollectionsEmpty = () => {
    return Object.values(results).every((icons) => icons.length === 0);
  };

  // Function to determine if an icon is from the basic collection
  const isBasicIcon = (collectionName) => {
    return collectionName === 'basic';
  };

  // Sort collections according to the defined order
  const getSortedCollections = () => {
    // Get all collections from results
    const collectionEntries = Object.entries(results);
    
    // Filter out empty collections
    const nonEmptyCollections = collectionEntries.filter(([_, icons]) => icons.length > 0);
    
    // Sort based on the predefined order
    return nonEmptyCollections.sort(([nameA], [nameB]) => {
      const indexA = collectionOrder.indexOf(nameA);
      const indexB = collectionOrder.indexOf(nameB);
      
      // If both collections are in our order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only one collection is in our order array, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // For collections not in our order array, maintain the original order
      return 0;
    });
  };

  return (
    <div className="container mx-auto">
      <style jsx>{`
        .basic3d-icon {
          max-width: 65% !important;
          max-height: 65% !important;
          width: 65% !important;
          height: 65% !important;
          transform: scale(1.3);
        }
        .standard-icon {
          max-width: 50%;
          max-height: 50%;
        }
        .basic-svg-icon {
          width: 40%;
          height: 40%;
          object-fit: contain;
        }
        .basic3d-container {
          padding: 0 !important;
        }
        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      {loading && <div className="text-center py-4">Loading results...</div>}
      {error && <div className="text-center text-red-500 py-4">{error}</div>}
      {!loading && !error && allCollectionsEmpty() && (
        <div className="text-center py-4">No results found.</div>
      )}

      {!loading && !error && !allCollectionsEmpty() && (
        <>
          {getSortedCollections().map(([collectionName, icons]) => {
            if (icons.length === 0) {
              // Пропускаем коллекции без иконок
              return null;
            }

            // Преобразуем имя коллекции для отображения заголовка
            const displayName = collectionName
              .split('-')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            const currentVisible =
              visibleIcons[collectionName] ||
              collectionInitialVisibleCount[collectionName] ||
              20;

            const totalIcons = icons.length;
            const hasMore = totalIcons > currentVisible;
            const iconsToShow = icons.slice(0, currentVisible);
            const remainingIcons = totalIcons - currentVisible;
            
            const isBasic3d = collectionName === 'basic3d';
            const isBasicSvg = isBasicIcon(collectionName);

            // Use the correct link path for the collection
            const collectionLink = collectionLinkPaths[collectionName] || `/${collectionName}-icons/`;

            return (
              <div key={collectionName} className="mb-4">
                <Link 
                  href={collectionLink}
                  className="text-sm mb-2 mt-4 inline-block underline"
                >
                  {displayName}
                </Link>
                
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2">
                  {iconsToShow.map((icon) => {
                    // Получаем конфигурацию для текущей коллекции
                    const collectionConfig = collectionUrlMap[collectionName];
                    // Формируем ссылку на картинку
                    const src = `${collectionConfig.baseUrl}${icon.slug}${collectionConfig.imageSuffix}`;
                    // Формируем ссылку на соответствующую страницу
                    const url = `${collectionConfig.urlPrefix}/${icon.slug}`;

                    return (
                      <div key={icon.id} className="rounded-lg">
                        <Link href={url} title={icon.title || 'Search Result'}>
                          <div className={`icon-container ${isBasic3d ? 'basic3d-container p-0' : 'p-1'} rounded-lg border border-gray-200 aspect-square group transition-transform duration-300`}>
                            {isBasicSvg ? (
                              // Special handling for basic SVG icons
                              <div className="w-full h-full flex items-center justify-center">
                                <img
                                  src={src}
                                  alt={icon.title || 'Search Result'}
                                  className="basic-svg-icon group-hover:scale-110 transition-transform duration-300"
                                />
                              </div>
                            ) : (
                              // Standard handling for other icons
                              <img
                                src={src}
                                alt={icon.title || 'Search Result'}
                                width="50"
                                height="50"
                                className={`${isBasic3d ? 'basic3d-icon' : 'standard-icon'} group-hover:scale-110 transition-transform duration-300`}
                                style={{
                                  maxWidth: isBasic3d ? '65% !important' : '50%',
                                  maxHeight: isBasic3d ? '65% !important' : '50%',
                                  width: isBasic3d ? '65% !important' : 'auto',
                                  height: isBasic3d ? '65% !important' : 'auto',
                                }}
                              />
                            )}
                          </div>
                        </Link>
                      </div>
                    );
                  })}

                  {hasMore && (
                    <div
                      className="flex justify-center items-center text-sm font-bold bg-white text-black p-3 border-2 border-black rounded cursor-pointer hover:bg-gray-100 transition-colors duration-300"
                      onClick={() => handleShowMore(collectionName)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleShowMore(collectionName);
                        }
                      }}
                    >
                      <span className="text-sm">Show {remainingIcons} more</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

SearchResults.propTypes = {
  query: PropTypes.string.isRequired,
  user: PropTypes.object,
};

export default SearchResults;